import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CategoryContext from "../../context/CategoryContext";
import { getPlacesByCategoryID } from "../../api";
import Fade from "@mui/material/Fade";
import "./CategoryPage.css";

const PAGE_SIZE = 9;

const CategoryPage = ({ t }) => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { setSelectedCategoryId } = useContext(CategoryContext);

  const [places, setPlaces] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const [fade, setFade] = useState(false);

  // Fetch function
  const fetchData = async (page, searchText) => {
    try {
      const data = await getPlacesByCategoryID(categoryId, {
        page,
        pageSize: PAGE_SIZE,
        search: searchText,
      });

      setPlaces(data.places || []);
      setPagination(data.pagination || {});

      if (data.places && data.places.length > 0) {
        setCategoryName(data.places[0].nameCategory);
      }

      setFade(true);
    } catch (error) {
      console.error("Error loading category:", error);
    }
  };

  useEffect(() => {
    if (!categoryId) return;

    setSelectedCategoryId(categoryId);
    fetchData(page, search);
  }, [categoryId, page, search]);

  const handleBack = () => navigate("/categories");
  const openPlace = (id) => navigate(`/place-details/${id}`);

  // Pagination number generator
  const generatePageNumbers = () => {
    const totalPages = Math.ceil((pagination?.total || 0) / PAGE_SIZE);
    let pages = [];

    if (totalPages <= 1) return pages;

    pages.push(1); // First page always visible

    if (page > 3) pages.push("...");

    for (let i = page - 1; i <= page + 1; i++) {
      if (i > 1 && i < totalPages) pages.push(i);
    }

    if (page < totalPages - 2) pages.push("...");

    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  return (
    <main className="page container category-page-wrapper fade-up">
      <Fade in={fade} timeout={800}>
        <div>
          {/* HEADER */}
          <header className="cp-header">
            <button className="cp-back-btn" onClick={handleBack}>
              ← {t("Back to Categories")}
            </button>

            <h1 className="cp-title">{t("Category Destinations")}</h1>

            {categoryName && (
              <h2 className="cp-subtitle">{t(categoryName)}</h2>
            )}

            {/* SEARCH */}
            <input
              type="text"
              className="cp-search-input"
              placeholder={t("Search places...")}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </header>

          {/* GRID */}
          <section className="cp-grid">
            {places.map((place) => (
              <article
                className="cp-card"
                key={place.PlaceID}
                onClick={() => openPlace(place.PlaceID)}
              >
                <div
                  className="cp-card-img"
                  style={{ backgroundImage: `url(${place.Image})` }}
                >
                  <div className="cp-card-img-overlay">
                    <h3>{t(place.PlaceName)}</h3>
                  </div>
                </div>

                <div className="cp-card-body">
                  <p className="cp-desc">{t(place.Description)}</p>

                  <div className="cp-meta">
                    <span>{t("City")}: {t(place.CityName)}</span>
                    <span>{t("Category")}: {t(place.nameCategory)}</span>
                  </div>
                </div>
              </article>
            ))}
          </section>

          {/* PAGINATION */}
          <div className="cp-pagination">

            {page > 1 && (
              <button className="cp-page-btn" onClick={() => setPage(1)}>
                {t("First")}
              </button>
            )}

            {page > 1 && (
              <button className="cp-page-btn" onClick={() => setPage(page - 1)}>
                {t("Previous")}
              </button>
            )}

            {generatePageNumbers().map((p, index) =>
              p === "..." ? (
                <span key={index} className="cp-page-dots">…</span>
              ) : (
                <button
                  key={index}
                  className={`cp-page-number ${p === page ? "active" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              )
            )}

            {page * PAGE_SIZE < pagination.total && (
              <button className="cp-page-btn" onClick={() => setPage(page + 1)}>
                {t("Next")}
              </button>
            )}

            {page < Math.ceil((pagination?.total || 0) / PAGE_SIZE) && (
              <button
                className="cp-page-btn"
                onClick={() =>
                  setPage(Math.ceil((pagination.total || 0) / PAGE_SIZE))
                }
              >
                {t("Last")}
              </button>
            )}
          </div>
        </div>
      </Fade>
    </main>
  );
};

export default CategoryPage;
